import { Clock, Wifi, Signal, CalendarCheck } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { relativeTime } from '@/lib/format';
import type { DeviceStatus } from '@/lib/types';

interface Props { device: DeviceStatus }

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="shrink-0 w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-sm font-semibold text-ink truncate">{value}</div>
      </div>
    </div>
  );
}

export function DeviceQuickStats({ device }: Props) {
  const rtcStr = [device.rtc?.time, device.rtc?.day, device.rtc?.date].filter(Boolean).join(' · ') || '—';
  const lastClean = device.lastCleaning?.time
    ? [device.lastCleaning.time, device.lastCleaning.day, device.lastCleaning.date].filter(Boolean).join(' · ')
    : 'Never';
  return (
    <Card>
      <CardBody className="divide-y divide-slate-100">
        <Stat icon={<Clock className="w-4 h-4" />}         label="Device time"    value={rtcStr} />
        <Stat icon={<CalendarCheck className="w-4 h-4" />} label="Last cleaning"  value={
          <>
            {lastClean}
            {device.lastCleaning?.status && <span className="ml-1 text-bad italic font-normal">({device.lastCleaning.status})</span>}
          </>
        } />
        <Stat icon={<Wifi className="w-4 h-4" />}          label="WiFi"           value={device.wifi?.ssid || '—'} />
        <Stat icon={<Signal className="w-4 h-4" />}        label="Signal · seen"  value={
          <>
            {device.wifi?.rssi !== undefined ? `${device.wifi.rssi} dBm` : '—'}
            <span className="text-slate-400 font-normal ml-2">{relativeTime(device.lastSeen ?? device.last_seen)}</span>
          </>
        } />
      </CardBody>
    </Card>
  );
}
